import { useState } from 'react';
import { useShowToast } from '@/hooks';
import { downloadFileFromUrl } from '@/utils/commonHelpers';
import {
  useDownloadReportMutation,
  useLazyPollDownloadReportsQuery,
} from '@/store/query/ats-reports/scheduleReportsEmail';
import { useSelector } from 'react-redux';
import { useKFlags } from './use-k-flags';

const MAX_RETRIES = 15;
const RETRY_DELAY = 3000;

export const useDownloadReports = (report, tile) => {
  const [downloadReport] = useDownloadReportMutation();
  const [pollReports] = useLazyPollDownloadReportsQuery();
  const [isPolling, setIsPolling] = useState(false);
  const toast = useShowToast();
  const isFlow = report?.subject_type === 'flow';
  const isStaticReport = report?.isStaticReport || false;
  const reportType = isStaticReport ? 'staticReports' : 'customReports';
  // Get table groups from Redux store
  const tableGroups = useSelector((state) => state[reportType]?.tableGroups);

  const { tableColData } = tile?.metadata ?? {};
  const { feReportsV3 } = useKFlags();

  const { order = [], visibility = {} } = tableColData ?? {};

  const tableColumnOrder = order
    ?.filter((key) => {
      // If no tile data or visibility is empty, show all columns (backward compatibility)
      if (!tile || Object.keys(visibility).length === 0) return true;

      // Check if the main column is visible
      const isMainColumnVisible = visibility?.[key];

      // For metric columns (ending with '_metric'), check if the parent column is visible
      if (key.endsWith('_metric')) {
        const parentKey = key.replace('_metric', '');
        return visibility?.[parentKey] && isMainColumnVisible;
      }

      return isMainColumnVisible;
    })
    .map((key) => {
      // Rename keys starting with 'pipeline_metric_' to 'metric'
      if (key.startsWith('pipeline_metric_')) {
        return 'metric';
      }
      return key;
    })
    .filter((key, index, array) => {
      // Remove 'metric' if it's the last element in the array
      if (key === 'metric' && index === array.length - 1) {
        return false;
      }
      return true;
    });

  const getMetricsValue = (metrics) => {
    if (metrics === 'passthrough') return 'passthrough_rate';
    if (metrics === 'overall') return 'overall_rate';
    return metrics || null;
  };

  const handleDownload = () => {
    const {
      timeFrame,
      subject_type,
      subject,
      group,
      selected,
      filters,
      passThroughMetric,
    } = report || {};

    const actualSubjectType = subject_type || subject;
    const reportSubjectType = isStaticReport ? actualSubjectType : 'custom';

    // live_job_pipeline does not use timeframe - exclude date, date_group, compare from payload
    const isLiveJobPipeline = actualSubjectType === 'live_job_pipeline';
    const queryFilterBy = isLiveJobPipeline
      ? (() => {
          const { date: _date, ...rest } = filters || {};
          return rest;
        })()
      : {
          date: {
            from: timeFrame?.from,
            to: timeFrame?.to,
          },
          ...filters,
        };

    const payload = {
      subject_type: reportSubjectType,
      ...(reportSubjectType === 'custom' && { custom_report_id: report?.id }),
      ...(reportSubjectType === 'eeoc' && {
        print_options: {
          scale: 0.95,
        },
      }),
      query: {
        filter_by: queryFilterBy,
        groups: {
          [`${reportSubjectType}_table_1`]: group,
        },
        ...(isLiveJobPipeline
          ? {}
          : {
              date_group: isFlow
                ? filters?.date_group || timeFrame?.date_group
                : timeFrame?.date_group,
            }),
      },
      metadata: {
        filtersToRender: selected,
        timeFrame,
        filters,
        ...(passThroughMetric ? { passThroughMetric } : {}),
        ...(reportSubjectType === 'custom' && {
          name: report?.name,
          description: report?.description,
        }),
        ...report?.metadata,
        tableGroups, // Add table groups to metadata
      },
    };

    setIsPolling(true);
    downloadReport({ payload }).then((response) => {
      if ('error' in response) {
        setIsPolling(false);
        toast({
          title: 'Unable to prepare the PDF',
          description: response?.error?.[0],
          status: 'error',
        });
      } else {
        const reportId = response?.data?.id;
        if (!reportId) {
          setIsPolling(false);
          toast({
            title: 'Unable to prepare the PDF',
            status: 'error',
          });
          return;
        }

        let retries = 0;

        const pollInterval = setInterval(() => {
          if (retries >= MAX_RETRIES) {
            clearInterval(pollInterval);
            setIsPolling(false);
            toast({
              title: 'Unable to prepare the PDF',
              description: 'The PDF took too long to prepare. Please try again',
              status: 'error',
            });
            return;
          }

          pollReports({ id: reportId }).then((pollResponse) => {
            if ('data' in pollResponse) {
              if (pollResponse?.data?.latest_report?.id) {
                clearInterval(pollInterval);
                setIsPolling(false);
                downloadFileFromUrl({
                  filename: pollResponse?.data?.latest_report?.filename,
                  url: pollResponse?.data?.latest_report?.url,
                  onSuccess: () => {
                    toast({
                      title: 'Your PDF is ready, and the download has started',
                    });
                  },
                  onError: () => {
                    toast({
                      title: 'Unable to download the PDF',
                      status: 'error',
                    });
                  },
                });
              } else if (pollResponse?.data?.last_error) {
                const msg = pollResponse?.data?.last_error?.msg;
                clearInterval(pollInterval);
                setIsPolling(false);
                toast({
                  title: 'Unable to prepare the PDF',
                  description: `The PDF preparation failed. ${msg}`,
                  status: 'error',
                });
              }
            }
          });

          retries++;
        }, RETRY_DELAY);
      }
    });

    return isPolling;
  };

  const handleDownloadCSV = () => {
    const {
      timeFrame,
      group = {
        group_by_1: null,
        group_by_2: null,
      },
      selected,
      filters,
      passThroughMetric,
      query_type = 'chart_table',
      drilldown,
      tile,
      metrics,
      sort_by_direction = 'asc',
      sort_by_column = null,
    } = report || {};
    const { subject_type, metadata } = tile || {};
    const sortColumn =
      sort_by_column === 'header' || sort_by_column === 'expander'
        ? group?.group_by_1 || null
        : sort_by_column;

    //hack for recruiter performance, kindly remove it after reportsv3 release
    let sortByColumn = sortColumn;
    if (
      !sortByColumn &&
      subject_type === 'recruiter_performance' &&
      !drilldown?.metric
    ) {
      sortByColumn = 'action_performed_by_user';
    }

    // live_job_pipeline does not use timeframe - exclude date, date_group, compare from payload
    const isLiveJobPipeline = subject_type === 'live_job_pipeline';
    const queryFilterBy = isLiveJobPipeline
      ? (() => {
          const { date: _date, ...rest } = filters || {};
          return rest;
        })()
      : {
          date: {
            from: timeFrame?.from,
            to: timeFrame?.to,
          },
          ...filters,
        };
    const queryBase = {
      filter_by: queryFilterBy,
      drilldown: {
        ...drilldown,
        ...(metadata?.id === 'offer_revision_per_offer' && {
          include_revisions: true,
        }),
      },
      groups: {
        [`${subject_type}_table_1`]: group,
      },
      ...(isLiveJobPipeline ? {} : { date_group: timeFrame?.date_group }),
      group_by_1: group?.group_by_1 ? group?.group_by_1 : null,
      group_by_2: group?.group_by_2 || null,
      metrics: getMetricsValue(metrics),
      sort_by_direction,
      sort_by_column: sortByColumn || null,
    };

    const payload = {
      export_type: 'csv',
      query_type,
      subject_type: subject_type,
      query: queryBase,
      metadata: {
        filtersToRender: selected,
        timeFrame,
        filters,
        ...(passThroughMetric ? { passThroughMetric } : {}),
        ...(subject_type === 'custom' && {
          name: report?.name,
          description: report?.description,
        }),
        ...report?.metadata,
        group, // Add table groups to metadata
      },

      ...(feReportsV3 &&
        Array.isArray(tableColumnOrder) &&
        tableColumnOrder.length > 0 && {
          table_columns: tableColumnOrder,
        }),
    };

    setIsPolling(true);

    downloadReport({ payload }).then((response) => {
      if ('error' in response) {
        setIsPolling(false);
        toast({
          title: 'Unable to prepare the CSV',
          description: response?.error?.[0],
          status: 'error',
        });
      } else {
        const reportId = response?.data?.id;
        let retries = 0;

        const pollInterval = setInterval(() => {
          if (retries >= MAX_RETRIES) {
            clearInterval(pollInterval);
            setIsPolling(false);
            toast({
              title: 'Unable to prepare the CSV',
              description: 'The CSV took too long to prepare. Please try again',
              status: 'error',
            });
            return;
          }

          pollReports({ id: reportId }).then((pollResponse) => {
            if ('data' in pollResponse) {
              if (pollResponse?.data?.latest_report?.id) {
                clearInterval(pollInterval);
                setIsPolling(false);
                downloadFileFromUrl({
                  filename: pollResponse?.data?.latest_report?.filename,
                  url: pollResponse?.data?.latest_report?.url,
                  onSuccess: () => {
                    toast({
                      title: 'Your CSV is ready, and the download has started',
                    });
                  },
                  onError: () => {
                    toast({
                      title: 'Unable to download the CSV',
                      status: 'error',
                    });
                  },
                });
              } else if (pollResponse?.data?.last_error) {
                const msg = pollResponse?.data?.last_error?.msg;
                clearInterval(pollInterval);
                setIsPolling(false);
                toast({
                  title: 'Unable to prepare the CSV',
                  description: `The CSV preparation failed. ${msg}`,
                  status: 'error',
                });
              }
            }
          });

          retries++;
        }, RETRY_DELAY);
      }
    });

    return isPolling;
  };

  return { handleDownload, handleDownloadCSV, isPolling };
};
